import { colors } from "@/constant/colors";
import { Reply } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import ExpandExplanation from "./expandExplanation";
import FillingQuestion from "./filling-question";
import NormalButton from "../Button/normal";
import VisualDiagram from "@/components/visual/VisualDiagram";
import { getQuestionPictureFallback, getQuestionVisual } from "@/lib/visual-diagram";

interface PracticeFillingProps {
  question: any;
  currentAnswer: any;
  allFinished: boolean;
  disabled: boolean;
  isReview?: boolean;
  className?: string;
  triggerCheck?: boolean;

  needButtons?: boolean;

  handleAnswer: (option: string, isCorrect: boolean, autoSubmit?: boolean) => void;
  onInputChange?: (value: string) => void;
}

function parseIfJsonLike(input: unknown) {
  if (typeof input !== 'string') return input;
  const s = input.trim();

  // 只在“像 JSON”时再尝试：对象/数组/用双引号包裹的 JSON 字符串
  const looksJson = s.startsWith('{') || s.startsWith('[') || (s.startsWith('"') && s.endsWith('"'));
  if (!looksJson) return input;

  try {
    return JSON.parse(s);
  } catch {
    return input;
  }
}

const PracticeFilling = ({ question, handleAnswer, currentAnswer, allFinished, disabled, isReview = false, className, onInputChange, triggerCheck, needButtons = true }: PracticeFillingProps) => {
  const questionVisual = getQuestionVisual(question);
  const questionPictureFallback = getQuestionPictureFallback(question);

  const blankCount = useMemo(() => {
    // Support both {blank} and [BLANK] formats
    if (!question || !question.question || typeof question.question !== 'string') {
      return 0;
    }
    return (question.question.match(/\{blank\}|\[BLANK\]/g) || []).length;
  }, [question?.question]);

  // userInput: 每个 blank 的内容
  const [userInput, setUserInput] = useState<string[]>(Array(blankCount).fill(""));
  // 当前正在输入的 blank 索引
  const [currentInputIdx, setCurrentInputIdx] = useState(0);
  // 当前输入框的内容
  const [inputValue, setInputValue] = useState("");

  const [submit, setSubmit] = useState(false);

  const standardAnswer = parseIfJsonLike(question.answer);

  useEffect(() => {
    if (currentAnswer.selectedOption === null) {
      handleReset();
    }
  }, [currentAnswer]);

  // 响应外部的Check触发
  useEffect(() => {
    if (triggerCheck && !submit && currentInputIdx < blankCount) {
      handleBlankInput();
    }
  }, [triggerCheck]);

  const handleReset = () => {
    setSubmit(false);
    setUserInput(Array(blankCount).fill(""));
    setCurrentInputIdx(0);
    setInputValue("");
  }


  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    if (onInputChange) {
      // 传递当前所有输入值给父组件
      const newUserInput = [...userInput];
      newUserInput[currentInputIdx] = e.target.value;
      onInputChange(newUserInput.join("|"));
    }
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (disabled) {
      return;
    }
    if (
      e.key === "Enter" &&
      inputValue.trim() !== "" &&
      currentInputIdx < blankCount
    ) {
      handleBlankInput();
    }
  };

  const handleBlankInput = () => {
    // 填入当前 blank
    const newUserInput = [...userInput];
    newUserInput[currentInputIdx] = inputValue.trim();
    setUserInput(newUserInput);

    if (currentInputIdx < blankCount - 1) {
      setCurrentInputIdx(currentInputIdx + 1);
      setInputValue("");
    } else {
      // 所有 blank 填满，直接提交
      setInputValue(""); // 清空输入框
      // 先尝试解析answer
      // 判断每一个输入内容是否都和answer[idx]相等
      const answers = Array.isArray(standardAnswer) ? standardAnswer : [standardAnswer];
      const isCorrect = newUserInput.every((val, idx) => {
        const expectedAnswer = answers[idx];
        if (!expectedAnswer) return false;
        return val.trim().toLowerCase() === String(expectedAnswer).trim().toLowerCase();
      });
      handleAnswer(
        newUserInput.join(""),
        isCorrect,
        true // autoSubmit = true
      );
      setSubmit(true);
    }
  };

  // 提交按钮逻辑
  const handleSubmit = () => {
    if (currentInputIdx < blankCount) {
      handleBlankInput();
    }
  };

  const isSubmitDisabled = disabled || submit;

  // Early return if no valid question
  if (!question || !question.question || typeof question.question !== 'string') {
    return (
      <div className="w-[370px] md:w-[600px] lg:w-[800px] h-full flex items-center justify-center">
        <div className="text-xl text-[#573F35]">Invalid question format</div>
      </div>
    );
  }

  return (
    <div className={`w-full h-full ${className}`}>
      <FillingQuestion className="emphasize-text" question={question.question} answer={standardAnswer} userInput={isReview ? [currentAnswer.selectedOption] : userInput} submit={submit} isReview={isReview} />
      {questionVisual && (
        <VisualDiagram
          visual={questionVisual}
          picture={questionPictureFallback}
          pictureAlt="Question diagram"
          context={typeof question?.question === 'string' ? question.question : ''}
        />
      )}
      {
        (!submit) && !isReview ? (
          <div className="flex flex-col justify-center items-center gap-2 mt-4">
            <span className="text-xl text-[#573F35] display-text">Fill in the Blanks</span>
            <div className="flex flex-row items-center gap-2">
              {currentInputIdx < blankCount && (
                <input
                  type="text"
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck="false"
                  className={`display-text w-[200px] h-10 rounded-lg bg-white p-2 border-2 ${currentAnswer.selectedOption !== null && !currentAnswer.isCorrect
                      ? 'border-[#FA6467]'
                      : 'border-gray-300'
                    }`}
                  value={inputValue}
                  style={{
                    fontSize: '20px',
                    background: '#F6F6F6',
                  }}
                  disabled={isSubmitDisabled}

                  onChange={handleInputChange}
                  onKeyDown={handleInputKeyDown}
                  placeholder={`Blank ${currentInputIdx + 1}`}
                  autoFocus
                />
              )}
              {
                needButtons && (
                  <NormalButton onClick={handleSubmit} disabled={isSubmitDisabled}>
                    <Reply className="w-5 h-5" color="#ffffff" />
                    <span className="text-white text-xl emphasize-text ml-2">Enter</span>
                  </NormalButton>
                )
              }
            </div>
          </div>
        ) : (
          <div className="flex flex-col justify-center items-center gap-2 h-[150px] mt-4">
            <div className="text-3xl text-[#573F35] display-text">Answer</div>
            <span className="text-xl text-[#573F35] display-text bg-[#BEFFDC] px-4 py-2">
              {Array.isArray(standardAnswer) ? standardAnswer.join(", ") : String(standardAnswer || '')}
            </span>
          </div>
        )
      }
      {currentAnswer.selectedOption !== null && (
        <ExpandExplanation explanation={question?.explanation || ''} initialHideExplanation={currentAnswer.isCorrect} />
      )}
    </div>
  );
};

export default PracticeFilling;
